import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect, TargetModeSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, X, Printer, ChevronLeft, Sparkles, CheckCircle2, Send } from "lucide-react";

const STATUS_DK = {
  taslak: { label: "Taslak", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  onaylandi: { label: "Onaylandı", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  yayinda: { label: "Yayında", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const subjectRows = (grade) => ["TÜRKÇE", "MATEMATİK", "FEN", (Number(grade) || 5) <= 4 ? "HAYAT BİLGİSİ" : "SOSYAL", "İNGİLİZCE"];
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

const emptyCard = (grade) => ({
  title: "", date: todayStr(),
  kimlik: { koc_ogretmen: "", hedef: "" },
  performans: { en_iyi_ders: "", en_zor_ders: "", zaman_problemi: "" },
  ozel_ders: { ders_adi: "", tarih: "" },
  konu_pekistirme: { konu_adi: "", kaynak: "", cozulen_soru: "" },
  soru_cozum: { ders_adi: "", kaynak_adi: "", toplam_soru: "", dogru: "" },
  kutuphane: { ders_adi: "", kaynak_adi: "", toplam_soru: "", dogru: "", yanlis: "" },
  sonuc_haritasi: subjectRows(grade).map((d) => ({ ders: d, dogru: "", yanlis: "" })),
  koc_tespit: subjectRows(grade).map((d) => ({ ders: d, kaynak: "", soru_sayisi: "" })),
  konu_tespit: subjectRows(grade).map((d) => ({ ders: d, eksik_konu: "", tekrar_tarih: "" })),
  toplam_odev: "",
});

export default function DenemeKarti() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [mode, setMode] = useState(params.get("grade") ? "class" : "student");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 1);
  const [clsSection, setClsSection] = useState(params.get("section") || "");
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [students, setStudents] = useState([]);
  const [cards, setCards] = useState([]);
  const [viewId, setViewId] = useState(null);
  const [draft, setDraft] = useState(null);

  const canEdit = user?.role === "admin" || user?.role === "koc";
  const isAdmin = user?.role === "admin";
  const isKoc = user?.role === "koc";

  useEffect(() => {
    if (user?.role === "ogrenci" || user?.role === "veli") {
      api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
    } else if (canEdit) {
      api.get("/students").then(({ data }) => setStudents(data));
    }
  }, [user, canEdit]);

  useEffect(() => {
    if (!canEdit || !studentId) return;
    const st = students.find((s) => s.id === studentId);
    if (st) setStudentName(st.name);
  }, [canEdit, studentId, students]);

  const load = useCallback(async () => {
    if (!studentId) return;
    const { data } = await api.get("/deneme-karti", { params: { student_id: studentId } });
    setCards(data);
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const setDeep = (sec, field, v) => setDraft((d) => ({ ...d, [sec]: { ...d[sec], [field]: v } }));
  const setRow = (list, i, field, v) => setDraft((d) => ({ ...d, [list]: d[list].map((r, j) => (j === i ? { ...r, [field]: v } : r)) }));

  const startNew = () => { setViewId(null); const g = mode === "class" ? clsGrade : (students.find((s) => s.id === studentId)?.grade || user?.grade); setDraft(emptyCard(g)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const startEdit = (card) => { setViewId(null); setDraft(JSON.parse(JSON.stringify(card))); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const save = async () => {
    if (mode === "class" && !draft?.id) {
      try {
        const { data } = await api.post("/deneme-karti/assign-class", { grade: clsGrade, class_section: clsSection || null, title: draft?.title || "", date: draft?.date || "" });
        toast.success(`${data.count} öğrenciye taslak kart oluşturuldu ✓ (onay/yayın öğrenci bazlı yapılır)`);
        setDraft(null);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (!studentId) return toast.error("Önce öğrenci seçin");
    try {
      let saved;
      if (draft.id) {
        const { data } = await api.put(`/deneme-karti/${draft.id}`, draft);
        saved = data;
        toast.success("Kart güncellendi ✓");
      } else {
        const { data } = await api.post("/deneme-karti", { ...draft, student_id: studentId });
        saved = data;
        toast.success("Kart oluşturuldu ✓");
      }
      setDraft(null);
      await load();
      setViewId(saved.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu deneme kartı silinsin mi?")) return;
    try { await api.delete(`/deneme-karti/${id}`); toast.success("Silindi"); if (viewId === id) setViewId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generate = async () => {
    if (!studentId) return toast.error("Önce öğrenci seçin");
    try {
      await api.post("/deneme-karti/generate", { student_id: studentId });
      toast.success("Eksiklere göre taslak kart oluşturuldu ✓ Yönetici onayı bekliyor.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approve = async (id) => {
    try { await api.post(`/deneme-karti/${id}/approve`); toast.success("Kart onaylandı ✓ Koç yayınlayabilir."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publish = async (id) => {
    try { await api.post(`/deneme-karti/${id}/publish`); toast.success("Kart yayınlandı ✓ Öğrenci ve veli görebilir."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const active = cards.find((c) => c.id === viewId);

  if (!canEdit && !studentId) return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Bağlı öğrenci bulunamadı.</div>;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600"><FileText className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Deneme Takip Kartı</h1>
            <p className="text-slate-500">Her deneme için özel takip kartı{studentName ? ` · ${studentName}` : ""}</p>
          </div>
        </div>
        {(draft || active) && (
          <button data-testid="dk-back-btn" onClick={() => { setDraft(null); setViewId(null); }} className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-600 shadow-sm transition hover:shadow-md active:scale-95">
            <ChevronLeft className="h-4 w-4" /> Kart Listesi
          </button>
        )}
      </div>

      {canEdit && !draft && !active && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm no-print">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci</label>
          <div className="flex flex-wrap items-center gap-3">
            <TargetModeSelect students={students} mode={mode} setMode={setMode} studentId={studentId} setStudentId={(v) => { setStudentId(v || null); setViewId(null); setDraft(null); }} grade={clsGrade} setGrade={setClsGrade} section={clsSection} setSection={setClsSection} testid="dk-student-select" />
            {(studentId || mode === "class") && (
              <div className="flex gap-2">
                {mode === "student" && (
                  <button data-testid="dk-generate-btn" onClick={generate} className="flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 font-bold text-white transition hover:bg-violet-700 active:scale-95">
                    <Sparkles className="h-5 w-5" /> Otomatik Oluştur
                  </button>
                )}
                <button data-testid="dk-new-btn" onClick={startNew} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95">
                  <Plus className="h-5 w-5" /> Yeni Kart
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kart listesi */}
      {!draft && !active && studentId && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm no-print">
          <h3 className="mb-4 font-display text-xl font-bold text-slate-900">Deneme Kartları</h3>
          {cards.length === 0 ? (
            <p data-testid="dk-empty" className="py-10 text-center text-slate-400">Henüz deneme kartı oluşturulmadı.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {cards.map((c) => (
                <div key={c.id} data-testid={`dk-card-${c.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                  <button onClick={() => setViewId(c.id)} className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900">{c.title || "Deneme Kartı"}</span>
                      {c.auto && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">Otomatik</span>}
                      <span data-testid={`dk-status-${c.id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${(STATUS_DK[c.status] || STATUS_DK.yayinda).cls}`}>{(STATUS_DK[c.status] || STATUS_DK.yayinda).label}</span>
                    </div>
                    <div className="text-xs text-slate-400">{fmtDate(c.date)}</div>
                  </button>
                  <div className="flex items-center gap-1.5">
                    {isAdmin && c.status === "taslak" && (
                      <button data-testid={`dk-approve-${c.id}`} onClick={() => approve(c.id)} className="flex items-center gap-1 rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600"><CheckCircle2 className="h-3.5 w-3.5" /> Onayla</button>
                    )}
                    {(isKoc || isAdmin) && c.status === "onaylandi" && (
                      <button data-testid={`dk-publish-${c.id}`} onClick={() => publish(c.id)} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white transition hover:bg-emerald-700"><Send className="h-3.5 w-3.5" /> Yayınla</button>
                    )}
                    <button data-testid={`dk-view-${c.id}`} onClick={() => setViewId(c.id)} className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-600 transition hover:bg-indigo-50">Görüntüle</button>
                    {canEdit && <>
                      <button data-testid={`dk-edit-${c.id}`} onClick={() => startEdit(c)} className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`dk-delete-${c.id}`} onClick={() => remove(c.id)} className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Düzenleme / görüntüleme */}
      {(draft || active) && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 no-print">
            {draft ? (
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Kart Adı</label>
                  <input data-testid="dk-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="ör. 1. Dönem 1. Deneme" className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Tarih</label>
                  <input data-testid="dk-date-input" type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
                </div>
              </div>
            ) : <div />}
            <div className="flex gap-2">
              <button data-testid="dk-print-btn" onClick={() => window.print()} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-700 shadow-sm transition hover:shadow-md active:scale-95">
                <Printer className="h-5 w-5" /> Yazdır / PDF
              </button>
              {draft ? (
                <>
                  <button data-testid="dk-cancel-btn" onClick={() => setDraft(null)} className="flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-semibold text-slate-600 transition hover:bg-slate-50"><X className="h-4 w-4" /> Vazgeç</button>
                  <button data-testid="dk-save-btn" onClick={save} className="rounded-xl bg-indigo-600 px-6 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Kaydet</button>
                </>
              ) : canEdit && (
                <button data-testid="dk-edit-btn" onClick={() => startEdit(active)} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Pencil className="h-4 w-4" /> Düzenle</button>
              )}
            </div>
          </div>
          <KartView data={draft || active} edit={!!draft} setDeep={setDeep} setRow={setRow} studentName={studentName} />
        </>
      )}
    </div>
  );
}

function KartView({ data, edit, setDeep, setRow, studentName }) {
  const F = ({ label, sec, field, type = "text", placeholder }) => (
    <div className="flex items-center gap-2 text-sm">
      <span className="whitespace-nowrap font-semibold text-slate-700">{label}</span>
      {edit ? (
        <input type={type} value={data[sec]?.[field] ?? ""} onChange={(e) => setDeep(sec, field, e.target.value)} placeholder={placeholder} className="w-full border-b border-dashed border-slate-300 bg-transparent px-1 py-0.5 outline-none focus:border-indigo-500" />
      ) : (
        <span className="min-h-[1.5rem] w-full border-b border-dashed border-slate-300 px-1 py-0.5 text-slate-800">{data[sec]?.[field] || "—"}</span>
      )}
    </div>
  );

  return (
    <div className="space-y-8">
      {/* KART 1 — Uygulama kayıtları */}
      <div data-testid="dk-card1" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <CardHeader />
        <div className="space-y-4">
          <Sec n="1" title="ÖZEL DERS KAYDI" border="border-emerald-700">
            <F label="Ders Adı:" sec="ozel_ders" field="ders_adi" />
            <F label="Ders Tarihi:" sec="ozel_ders" field="tarih" type="date" />
          </Sec>
          <Sec n="2" title="KONU PEKİŞTİRME ÇALIŞMASI" border="border-emerald-700">
            <F label="Konu Adı:" sec="konu_pekistirme" field="konu_adi" />
            <F label="Kullanılan Kaynak:" sec="konu_pekistirme" field="kaynak" />
            <F label="Çözülen Soru Sayısı:" sec="konu_pekistirme" field="cozulen_soru" type="number" />
          </Sec>
          <Sec n="3" title="SORU ÇÖZÜM UYGULAMASI" border="border-blue-900">
            <F label="Ders Adı:" sec="soru_cozum" field="ders_adi" />
            <F label="Kaynak Adı:" sec="soru_cozum" field="kaynak_adi" />
            <div className="grid grid-cols-2 gap-3">
              <F label="Toplam Soru:" sec="soru_cozum" field="toplam_soru" type="number" />
              <F label="Doğru Sayısı:" sec="soru_cozum" field="dogru" type="number" />
            </div>
          </Sec>
          <Sec n="4" title="KÜTÜPHANE UYGULAMASI" border="border-stone-400">
            <F label="Ders Adı:" sec="kutuphane" field="ders_adi" />
            <F label="Kaynak Adı:" sec="kutuphane" field="kaynak_adi" />
            <div className="grid grid-cols-3 gap-3">
              <F label="Toplam Soru:" sec="kutuphane" field="toplam_soru" type="number" />
              <F label="Doğru Sayısı:" sec="kutuphane" field="dogru" type="number" />
              <F label="Yanlış Sayısı:" sec="kutuphane" field="yanlis" type="number" />
            </div>
          </Sec>
        </div>
      </div>

      {/* KART 2 — Analiz ve haritalar */}
      <div data-testid="dk-card2" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <CardHeader />
        <div className="grid gap-4 md:grid-cols-2">
          <Box title="ÖĞRENCİ KİMLİK BİLGİLERİ">
            <div className="flex items-center gap-2 text-sm">
              <span className="whitespace-nowrap font-semibold text-slate-700">Öğrenci Adı:</span>
              <span className="min-h-[1.5rem] w-full border-b border-dashed border-slate-300 px-1 py-0.5 text-slate-800">{studentName || "—"}</span>
            </div>
            <F label="Koç Öğretmen:" sec="kimlik" field="koc_ogretmen" />
            <F label="Hedef Lise / Hedef Üniversite:" sec="kimlik" field="hedef" />
            <div className="mt-2 rounded-lg bg-[#232a4d] px-3 py-2 text-center text-sm italic text-white">Kendi Gelişimini Takip Et, Başarıya Ulaş!</div>
          </Box>
          <Box title="PERFORMANS ANALİZİ">
            <F label="Bu Denemede En İyi Dersim:" sec="performans" field="en_iyi_ders" />
            <F label="En Çok Zorlandığım Ders:" sec="performans" field="en_zor_ders" />
            <F label="Zaman Problemi Yaşadığım Bölüm:" sec="performans" field="zaman_problemi" />
          </Box>
          <MapTable title="DENEME SONUÇ HARİTASI" cols={[{ key: "dogru", label: "DOĞRU" }, { key: "yanlis", label: "YANLIŞ" }]} rows={data.sonuc_haritasi || []} listKey="sonuc_haritasi" edit={edit} setRow={setRow} />
          <MapTable title="KOÇ TESPİT HARİTASI" cols={[{ key: "kaynak", label: "KAYNAK" }, { key: "soru_sayisi", label: "SORU SAYISI" }]} rows={data.koc_tespit || []} listKey="koc_tespit" edit={edit} setRow={setRow} />
        </div>

        <div className="mt-4">
          <MapTable title="KONU TESPİT HARİTASI" cols={[{ key: "eksik_konu", label: "EKSİK KONU" }, { key: "tekrar_tarih", label: "TEKRAR TARİH" }]} rows={data.konu_tespit || []} listKey="konu_tespit" edit={edit} setRow={setRow} dateFields={["tekrar_tarih"]} />
          <div className="mt-3 flex items-center justify-end gap-2 text-sm font-bold text-slate-800">
            <span>TOPLAM ÖDEV:</span>
            {edit ? (
              <div className="flex items-center gap-1">
                <input data-testid="dk-toplam-odev" type="number" min={0} value={data.toplam_odev ?? ""} onChange={(e) => setDeep("toplam_odev", null, e.target.value)} className="w-24 rounded border border-slate-300 px-2 py-1 text-center outline-none focus:border-indigo-400" />
                <span>Soru</span>
              </div>
            ) : <span className="border-b border-dashed border-slate-400 px-4">{data.toplam_odev || "—"} Soru</span>}
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-[#232a4d] p-5 text-white">
          <div className="mb-3 font-display text-base font-extrabold tracking-wide">SINAV STRATEJİSİ</div>
          <div className="grid gap-x-8 gap-y-1.5 text-sm sm:grid-cols-2">
            {["Soruyu iki kez oku.", "Zor soruya takılma.", "Turlama tekniğini kullan.", "Süreyi kontrol et.", "Panik yapma.", "İşaretlemeyi kontrol et."].map((t) => (
              <div key={t} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-white/80" /> {t}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CardHeader() {
  return (
    <div className="mb-5 flex items-center justify-between border-b-2 border-slate-100 pb-3">
      <span className="font-display text-lg font-extrabold tracking-widest text-slate-800">ZOR</span>
      <span className="font-display text-xl font-extrabold tracking-wide text-slate-900">DENEME TAKİP KARTI <span className="text-indigo-700">2026</span></span>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">ZOR Academy</span>
    </div>
  );
}

function Sec({ n, title, border, children }) {
  return (
    <div className={`relative overflow-hidden rounded-lg border-2 ${border} p-4 sm:pl-20`}>
      <div className="absolute -left-1 top-1/2 hidden -translate-y-1/2 select-none font-display text-7xl font-extrabold text-slate-200 sm:block">{n}</div>
      <div className="mb-3 text-center font-display text-base font-extrabold tracking-wide text-slate-800">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Box({ title, children }) {
  return (
    <div className="rounded-lg border-2 border-emerald-800 p-4">
      <div className="mb-3 text-center font-display text-sm font-extrabold tracking-wide text-slate-800">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function MapTable({ title, cols, rows, listKey, edit, setRow, dateFields = [] }) {
  return (
    <div className="overflow-hidden rounded-lg border-2 border-emerald-800">
      <div className="bg-emerald-900/5 px-3 py-2 text-center font-display text-sm font-extrabold tracking-wide text-slate-800">{title}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#232a4d] text-xs font-bold tracking-wide text-white">
            <th className="px-2 py-1.5 text-left">DERS</th>
            {cols.map((c) => <th key={c.key} className="px-2 py-1.5">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.ders || i} className="border-t border-slate-200">
              <td className="px-2 py-1.5 font-semibold text-slate-800">{r.ders}</td>
              {cols.map((c) => (
                <td key={c.key} className="px-2 py-1">
                  {edit ? (
                    <input data-testid={`dk-${listKey}-${i}-${c.key}`} type={dateFields.includes(c.key) ? "date" : "text"} value={r[c.key] ?? ""} onChange={(e) => setRow(listKey, i, c.key, e.target.value)} className="w-full rounded border border-slate-200 px-1.5 py-1 text-center outline-none focus:border-indigo-400" />
                  ) : (
                    <div className="min-h-[1.5rem] border-b border-dashed border-slate-200 text-center text-slate-800">{c.key === "tekrar_tarih" ? fmtDate(r[c.key]) : (r[c.key] || "—")}</div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
